/**
 * A document section's status, shown the same way on every document panel.
 *
 * SectionStatusBadge is the small pill on the section row. SectionFailureNotice
 * sits under the row of a section that could not be generated, with the stored
 * reasons and what to do. The words come from features/documents/sectionStatus.
 * The backend stores a plain reason for the screen and the raw system text in
 * a separate field (failureDetail / generationErrorDetail); the raw text is
 * kept in the line's tooltip only.
 */
import { AlertTriangle } from 'lucide-react';
import { sectionStatusView, type SectionStatusInput } from '../../features/documents/sectionStatus';

const OK_STYLES: Record<string, string> = {
  Generated: 'bg-blue-100 text-blue-700',
  Edited: 'bg-amber-100 text-amber-700',
  Reviewed: 'bg-green-100 text-green-700',
  Approved: 'bg-green-100 text-green-700',
  'In review': 'bg-blue-100 text-blue-700',
};

export function SectionStatusBadge({ section }: { section: SectionStatusInput }) {
  const view = sectionStatusView(section);
  if (!view.label) return null;
  const style =
    view.kind === 'failed'
      ? 'bg-red-100 text-red-700 border border-red-200'
      : view.kind === 'not_applicable'
        ? 'bg-gray-100 text-gray-500'
        : OK_STYLES[view.label] || 'bg-gray-100 text-gray-600';
  return (
    <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full whitespace-nowrap ${style}`}>
      {view.label}
    </span>
  );
}

export function SectionFailureNotice({
  section,
  regenerateHint,
}: {
  section: SectionStatusInput;
  /** What to do, naming this panel's own regenerate button. */
  regenerateHint?: string;
}) {
  const view = sectionStatusView(section, { regenerateHint });
  if (view.kind !== 'failed') return null;
  return (
    <div className="mx-4 mb-3 ml-11 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800">
      <p className="flex items-center gap-1.5 font-semibold">
        <AlertTriangle className="h-3.5 w-3.5 flex-shrink-0" />
        {view.label}
      </p>
      {view.notice.length > 0 && (
        <ul className="mt-1 ml-5 list-disc space-y-0.5">
          {view.notice.map((line) => (
            <li key={line.text} title={line.detail}>
              {line.text}
            </li>
          ))}
        </ul>
      )}
      <p className="mt-1 text-red-700">{view.hint}</p>
    </div>
  );
}
